/** Business KPIs page - comprehensive dashboard with financial, quality, and volume metrics. */
import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Refresh,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { businessKpisApi } from "../api/client";

interface FinancialKPIs {
  revenue: number;
  cost: number;
  profit: number;
  profit_margin_percent: number;
  total_calls: number;
  answered_calls: number;
  avg_revenue_per_call: number;
  revenue_trend?: string;
  profit_trend?: string;
  cost_trend?: string;
  previous_period_label?: string;
}

interface QualityKPIs {
  asr_percent: number;
  acd_seconds: number;
  pdd_seconds: number;
  ner_percent: number;
  total_attempts: number;
  answered_calls: number;
  failed_calls: number;
  asr_trend?: string;
  acd_trend?: string;
  pdd_trend?: string;
  previous_period_label?: string;
}

interface VolumeKPIs {
  total_calls: number;
  answered_calls: number;
  attempted_calls: number;
  active_calls?: number;
  max_cps?: number;
  answer_rate_percent: number;
  calls_trend?: string;
  previous_period_label?: string;
}

interface ProviderQualityAlert {
  carrier: string;
  asr_percent: number;
  acd_seconds: number;
  pdd_seconds: number;
  asr_alert: boolean;
  acd_alert: boolean;
  has_alert: boolean;
}

interface KPISummary {
  period: string;
  start_time: string;
  end_time: string;
  financial?: FinancialKPIs;
  quality?: QualityKPIs;
  volume?: VolumeKPIs;
}

interface KPICard {
  title: string;
  value: string | number;
  subtitle: string;
  trend?: string;
  color: string;
  icon?: React.ReactNode;
}

/** Business KPIs page component. */
const BusinessKPIs: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<"financial" | "quality" | "volume">("financial");
  const [period, setPeriod] = useState<string>("last_7_days");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<KPISummary | null>(null);
  const [alerts, setAlerts] = useState<ProviderQualityAlert[]>([]);

  const fetchKPIs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await businessKpisApi.getKPIs({
        period,
        tab: activeTab,
      });
      setKpis(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load KPIs");
      console.error("Error fetching KPIs:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await businessKpisApi.getProviderAlerts();
      setAlerts(response.data);
    } catch (err) {
      console.error("Error fetching alerts:", err);
      // Set empty alerts array on error - the UI will show "No alerts" message
      setAlerts([]);
    }
  };

  useEffect(() => {
    fetchKPIs();
  }, [period, activeTab]);

  // TODO: Re-enable provider alerts when backend endpoint is fixed
  // useEffect(() => {
  //   fetchAlerts();
  //   const interval = setInterval(fetchAlerts, 30000);
  //   return () => clearInterval(interval);
  // }, []);

  const renderTrend = (trend?: string) => {
    if (!trend || trend === "N/A") return null;
    const isPositive = trend.startsWith("+");
    const color = isPositive ? "success" : "error";
    const icon = isPositive ? <TrendingUp /> : <TrendingDown />;

    return (
      <Chip
        icon={icon}
        label={trend}
        size="small"
        color={color as any}
        variant="outlined"
        sx={{ ml: 1 }}
      />
    );
  };

  const renderFinancialKPIs = () => {
    if (!kpis?.financial) return null;

    const toNumber = (val: number | string): number => typeof val === "string" ? parseFloat(val) : val;
    // Backend returns nested KPIMetric {value, trend} — normalize to number
    const mv = (m: any): number => (m && typeof m === "object" ? Number(m.value ?? 0) : Number(m ?? 0));

    const cards: KPICard[] = [
      {
        title: "Total Revenue",
        value: `$${mv(kpis.financial.revenue).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
        subtitle: kpis.financial.previous_period_label || "",
        trend: kpis.financial.revenue_trend,
        color: theme.palette.success.main,
      },
      {
        title: "Total Cost",
        value: `$${mv(kpis.financial.cost).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
        subtitle: kpis.financial.previous_period_label || "",
        trend: kpis.financial.cost_trend,
        color: theme.palette.error.main,
      },
      {
        title: "Profit",
        value: `$${mv(kpis.financial.profit).toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
        subtitle: `${Number(kpis.financial.profit_margin_percent ?? 0).toFixed(1)}% margin`,
        trend: kpis.financial.profit_trend,
        color: mv(kpis.financial.profit) >= 0 ? theme.palette.primary.main : theme.palette.error.main,
      },
      {
        title: "Avg Revenue/Call",
        value: `$${mv(kpis.financial.avg_revenue_per_call).toFixed(4)}`,
        subtitle: `${kpis.financial.answered_calls} answered calls`,
        color: theme.palette.warning.main,
      },
    ];

    return (
      <Grid container spacing={3}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {card.title}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h5" component="div" sx={{ color: card.color, fontWeight: "bold" }}>
                    {card.value}
                  </Typography>
                  {renderTrend(card.trend)}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderQualityKPIs = () => {
    if (!kpis?.quality) return null;

    const toNumber = (val: number | string): number => typeof val === "string" ? parseFloat(val) : val;
    // Backend returns nested KPIMetric {value, trend} — normalize to number
    const mv = (m: any): number => (m && typeof m === "object" ? Number(m.value ?? 0) : Number(m ?? 0));

    const cards: KPICard[] = [
      {
        title: "Answer-Seizure Ratio",
        value: `${mv(kpis.quality.asr_percent).toFixed(1)}%`,
        subtitle: `${kpis.quality.answered_calls}/${kpis.quality.total_attempts} answered`,
        trend: kpis.quality.asr_trend,
        color: mv(kpis.quality.asr_percent) >= 60 ? theme.palette.success.main : theme.palette.error.main,
      },
      {
        title: "Avg Call Duration",
        value: `${mv(kpis.quality.acd_seconds).toFixed(0)}s`,
        subtitle: kpis.quality.previous_period_label || "",
        trend: kpis.quality.acd_trend,
        color: mv(kpis.quality.acd_seconds) >= 120 ? theme.palette.success.main : theme.palette.warning.main,
      },
      {
        title: "Post Dial Delay",
        value: `${mv(kpis.quality.pdd_seconds).toFixed(2)}s`,
        subtitle: kpis.quality.previous_period_label || "",
        trend: kpis.quality.pdd_trend,
        color: mv(kpis.quality.pdd_seconds) <= 3 ? theme.palette.success.main : theme.palette.error.main,
      },
      {
        title: "Network Efficiency",
        value: `${mv(kpis.quality.ner_percent).toFixed(1)}%`,
        subtitle: `${kpis.quality.failed_calls} failed calls`,
        color: theme.palette.primary.main,
      },
    ];

    return (
      <Grid container spacing={3}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {card.title}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h5" component="div" sx={{ color: card.color, fontWeight: "bold" }}>
                    {card.value}
                  </Typography>
                  {renderTrend(card.trend)}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderVolumeKPIs = () => {
    if (!kpis?.volume) return null;

    const toNumber = (val: number | string): number => typeof val === "string" ? parseFloat(val) : val;
    // Backend returns nested KPIMetric {value, trend} — normalize to number
    const mv = (m: any): number => (m && typeof m === "object" ? Number(m.value ?? 0) : Number(m ?? 0));

    const cards: KPICard[] = [
      {
        title: "Total Calls",
        value: mv(kpis.volume.total_calls).toLocaleString(),
        subtitle: kpis.volume.previous_period_label || "",
        trend: kpis.volume.calls_trend,
        color: theme.palette.primary.main,
      },
      {
        title: "Answered Calls",
        value: mv(kpis.volume.answered_calls).toLocaleString(),
        subtitle: `${mv(kpis.volume.answer_rate_percent).toFixed(1)}% answer rate`,
        color: theme.palette.success.main,
      },
      {
        title: "Attempted Calls",
        value: mv(kpis.volume.attempted_calls).toLocaleString(),
        subtitle: "Total call attempts",
        color: theme.palette.warning.main,
      },
      {
        title: "Active Calls",
        value: kpis.volume.active_calls?.toLocaleString() || "N/A",
        subtitle: `Max CPS: ${kpis.volume.max_cps || "N/A"}`,
        color: "#9c27b0",
      },
    ];

    return (
      <Grid container spacing={3}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {card.title}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h5" component="div" sx={{ color: card.color, fontWeight: "bold" }}>
                    {card.value}
                  </Typography>
                  {renderTrend(card.trend)}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {card.subtitle}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderProviderAlerts = () => {
    if (alerts.length === 0) {
      return (
        <Alert severity="success" icon={<CheckCircle />}>
          No provider quality alerts detected. All carriers are performing within acceptable thresholds.
        </Alert>
      );
    }

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
            <Warning color="error" sx={{ mr: 1 }} />
            Provider Quality Alerts ({alerts.length})
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Carrier</TableCell>
                  <TableCell align="right">ASR</TableCell>
                  <TableCell align="right">ACD</TableCell>
                  <TableCell align="right">PDD</TableCell>
                  <TableCell align="center">Alerts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((alert) => {
                  const toNumber = (val: number | string): number => typeof val === "string" ? parseFloat(val) : val;
    // Backend returns nested KPIMetric {value, trend} — normalize to number
    const mv = (m: any): number => (m && typeof m === "object" ? Number(m.value ?? 0) : Number(m ?? 0));
                  return (
                    <TableRow key={alert.carrier} hover sx={{ bgcolor: alert.has_alert ? "warning.light" : "inherit" }}>
                      <TableCell>{alert.carrier}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${toNumber(alert.asr_percent).toFixed(1)}%`}
                          size="small"
                          color={alert.asr_alert ? "error" : "success"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${toNumber(alert.acd_seconds).toFixed(0)}s`}
                          size="small"
                          color={alert.acd_alert ? "error" : "success"}
                        />
                      </TableCell>
                      <TableCell align="right">{toNumber(alert.pdd_seconds).toFixed(2)}s</TableCell>
                      <TableCell align="center">
                        {alert.asr_alert && <Chip label="ASR" size="small" color="error" sx={{ mr: 0.5 }} />}
                        {alert.acd_alert && <Chip label="ACD" size="small" color="error" />}
                        {!alert.asr_alert && !alert.acd_alert && <Chip label="OK" size="small" color="success" />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const handleRefresh = () => {
    fetchKPIs();
    fetchAlerts();
  };

  const periodOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last_7_days", label: "Last 7 Days" },
    { value: "last_30_days", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Business KPIs
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={period}
              label="Period"
              onChange={(e) => setPeriod(e.target.value)}
            >
              {periodOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs for KPI categories */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label="Financial" value="financial" />
          <Tab label="Quality" value="quality" />
          <Tab label="Volume" value="volume" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPI Cards based on active tab */}
          {activeTab === "financial" && renderFinancialKPIs()}
          {activeTab === "quality" && renderQualityKPIs()}
          {activeTab === "volume" && renderVolumeKPIs()}

          {/* Provider Quality Alerts - always show */}
          <Box mt={3}>
            {renderProviderAlerts()}
          </Box>
        </>
      )}
    </Container>
  );
};

export default BusinessKPIs;
